import {
  CalendarDotsIcon,
  PaintRollerIcon,
  ChartBarIcon,
  PlugsIcon,
  BookIcon,
  CardsThreeIcon
} from '@phosphor-icons/react';
import ReportsSidebar from './ReportsSidebar';
import SettingsSidebar from './SettingsSidebar';
import TeamSwitcher from './TeamSwitcher';
import NavLinks, { type LinkSection } from './NavLinks';
import LiveDemoCard from './LiveDemoCard';
import SidenavFooter from './SidenavFooter';
import UpgradeBanner from './UpgradeBanner';
import { useEffect, useState, useRef } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import Workspace from './Workspace';
import { useDisclosure } from '@heroui/react';
import { getTeamMemberServiceSide, getUserEmailByUcc, inviteTeamMember, updateTeamMemberPublic, updateUserUcc, requestProTrial, getProTrialStatus } from '@services/youtube-platform.service';
import { notify } from '@components/Presentation/helpers';
import { getAuth, setCurrentTeamToken } from '@services/identity.service';
import { usePopupContext } from '@components/context/screenShotPupUpContext';
import ScreenShotsPopup from '@components/Screenshot-Popup';
import { useOffice } from '@components/OfficeLoader';
import { useWindowSize } from '@uidotdev/usehooks';
import { usePurchasePlan } from '@lib/hooks/usePlanSettings';
import { useTeamsData } from '@lib/hooks/useTeamsData';

export default function Sidenav() {
  const router = useRouter();
  const pathname = usePathname();

  const { isPowerpoint } = useOffice();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {
    isOpen,
    onOpen,
    onClose,
    onOpenChange
  } = useDisclosure();
  const { isPopupVisible } = usePopupContext();
  const { width } = useWindowSize();

  const { doLogout, ownTeam, currentTeam, teams, selectTeam, user, currentTeamBillingAccountOwnerID, initTeam } = useTeamsData((s) => ({ doLogout: s.doLogout, ownTeam: s.ownTeam, currentTeam: s.currentTeam, teams: s.teams, selectTeam: s.selectTeam, user: s.user, currentTeamBillingAccountOwnerID: s.currentTeamBillingAccountOwnerID, initTeam: s.initTeam }));

  const [isExpanded, setIsExpanded] = useState(true);
  const [sidebarView, setSidebarView] = useState<'main' | 'reports' | 'settings'>('main');
  const [previousPath, setPreviousPath] = useState<string>('/');
  const lastMainPath = useRef<string>('/');
  const teamName = currentTeam?.billing_account?.name;

  const domains = ['@streamalive', '@cello.so'];
  const [isInternalUser, setIsInternalUser] = useState(false);
  const isEmailIncluded = (email: string) => {
    const emailDomain = email?.substring(email?.indexOf('@'));
    const check = domains?.some((domain) => emailDomain?.startsWith(domain));
    setIsInternalUser(check);
  };

  const linkSections: LinkSection[][] = [
    [
      {
        name: 'My presentations',
        url: '/presentations',
        icon: <CardsThreeIcon size={20} />,
        show: true
      },
      {
        name: 'Interaction library',
        url: '/interaction-links',
        icon: <BookIcon size={20} />,
        show: true
      },
    ],
    [
      {
        name: 'Sessions',
        url: '/sessions',
        icon: <CalendarDotsIcon size={20} />,
        show: !isPowerpoint
      },
    ],
    [
      {
        name: 'Themes',
        url: '/themes',
        icon: <PaintRollerIcon size={20} />,
        show: true
      },
      {
        name: 'Reports',
        url: '/stats',
        icon: <ChartBarIcon size={20} />,
        show: true
      },
      {
        name: 'Integrations',
        url: '/integrations',
        icon: <PlugsIcon size={20} />,
        show: !isPowerpoint
      }
    ],
  ];

  const widthClass = isExpanded ? 'w-[300px] max-w-[300px] min-w-[300px]' : 'w-[64px] max-w-[64px] min-w-[64px]';

  useEffect(() => {
    if (!width || !pathname) return;

    if (isPowerpoint) {
      setIsExpanded(false);
      return;
    }

    if (!['/stats/', '/pricing'].some((path) => pathname.startsWith(path))) {
      setIsExpanded(true);
    }

    if (['/stats/'].some((path) => pathname.startsWith(path))) {
      setIsExpanded(true);
    }

    if (['/pricing'].some((path) => pathname.startsWith(path))) {
      if (width >= 1440) {
        setIsExpanded(true);
      } else {
        setIsExpanded(false);
      }
    }
  }, [width, pathname, isPowerpoint]);

  useEffect(() => {
    if (currentTeam?.email) isEmailIncluded(currentTeam.email);
  }, [currentTeam]);

  // Persist sidebar view based on current route
  useEffect(() => {
    const currentView = (() => {
      if (pathname?.startsWith('/stats/')) {
        return 'reports';
      } else if (pathname === '/settings') {
        return 'settings';
      } else {
        return 'main';
      }
    })();

    // Track the last main path for back navigation
    if (currentView === 'main' && pathname) {
      lastMainPath.current = pathname;
    }

    // When entering a sub-view, save the last main path as previousPath
    if (currentView !== 'main' && sidebarView === 'main') {
      setPreviousPath(lastMainPath.current);
    }

    setSidebarView(currentView);
  }, [pathname, isPowerpoint]);

  const auth = getAuth();
  const getActiveUcc = async () => {
    const { ucc } = await (window as any).Cello('getActiveUcc');

    const res = await getUserEmailByUcc(ucc);

    if (!res?.entity?.email) {
      await updateUserUcc(auth.id, ucc);
    }
  };

  const { name, settings, isFreePlan, isProTrial } = usePurchasePlan(currentTeamBillingAccountOwnerID);
  const showBanner = isFreePlan;
  const showProTrialButton = isFreePlan || isProTrial;
  const liveEventsUrl = 'https://events.streamalive.com/';

  const [hasRequestedTrial, setHasRequestedTrial] = useState(false);
  const [isRequestingTrial, setIsRequestingTrial] = useState(false);
  const [isApprovedTrial, setIsApprovedTrial] = useState(false);

  useEffect(() => {
    if (!currentTeamBillingAccountOwnerID) return;
    getProTrialStatus().then((res: any) => {
      if (res?.entity?.requested) setHasRequestedTrial(true);
      if (res?.entity?.approved) setIsApprovedTrial(true);
    }).catch(() => { });
  }, [currentTeamBillingAccountOwnerID]);

  const handleRequestProTrial = async () => {
    if (hasRequestedTrial || isRequestingTrial) return;
    setIsRequestingTrial(true);
    try {
      await requestProTrial();
      setHasRequestedTrial(true);
      notify('Thanks! We\'ve received your request and will review it shortly. You\'ll hear from us via email soon.', 'success');
    } catch {
      notify('Something went wrong. Please try again.', 'error');
    } finally {
      setIsRequestingTrial(false);
    }
  };

  const searchParams = useSearchParams();

  useEffect(() => {
    if (searchParams?.get('team')) {
      setIsDropdownOpen(true);
    }
  }, [searchParams]);

  const fetchAndAcceptInvite = async (id: string) => {
    try {
      const result = await getTeamMemberServiceSide(
        id,
        id
      );
      const teamInfo = result.entity;

      if (!user) return;

      const payload = { accepted: true, user_id: user.id };
      const response = await updateTeamMemberPublic(
        teamInfo.id,
        teamInfo.billing_account.id,
        payload
      );

      if (response.status && response.entity.token) {
        await setCurrentTeamToken(response.entity.token);
        await inviteTeamMember();
        router.replace('/presentations');
      }
    } catch (err) {
      console.error('Error processing team invite:', err);
    }
  };

  useEffect(() => {
    if (!searchParams?.get('invite') || !searchParams?.get('id')) return;
    fetchAndAcceptInvite(searchParams.get('id')!);
    initTeam();
  }, [user, searchParams]);

  return (
    <>
      <header className={`${widthClass} bg-content2 pb-4 flex flex-col h-screen overflow-auto relative border-r border-divider`}>

        {sidebarView === 'main' && (
          <TeamSwitcher
            isExpanded={isExpanded}
            isPowerpoint={isPowerpoint}
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            teamName={teamName}
            currentTeam={currentTeam}
            teams={teams}
            planName={name}
            router={router}
            selectTeam={selectTeam}
            doLogout={doLogout}
            onOpenWorkspaceSettings={onOpen}
            getActiveUcc={getActiveUcc}
          />
        )}

        {sidebarView === 'main' && (
          <NavLinks linkSections={linkSections} isExpanded={isExpanded} pathname={pathname} />
        )}

        {sidebarView === 'main' && isExpanded && (
          <LiveDemoCard liveEventsUrl={liveEventsUrl} />
        )}

        {sidebarView === 'main' && showBanner && isExpanded && <UpgradeBanner />}

        {sidebarView === 'main' && isExpanded && (
          <SidenavFooter
            showProTrialButton={showProTrialButton}
            hasRequestedTrial={hasRequestedTrial}
            isRequestingTrial={isRequestingTrial}
            isApprovedTrial={isApprovedTrial}
            onRequestProTrial={handleRequestProTrial}
          />
        )}

        {sidebarView === 'reports' && (
          <ReportsSidebar
            isExpanded={isExpanded}
            onBack={() => {
              router.push('/stats');
            }}
          />
        )}

        {sidebarView === 'settings' && (
          <SettingsSidebar
            isExpanded={isExpanded}
            onBack={() => {
              router.push(previousPath);
            }}
          />
        )}

        {isOpen && (
          <Workspace
            isOpen={isOpen}
            onClose={onClose}
            onOpenChange={onOpenChange}
          />
        )}
      </header >

      {isPopupVisible && !isPowerpoint &&
        <ScreenShotsPopup />
      }
    </>
  );
}
